import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import 'antd/dist/reset.css';
import MapLeak from './components/map';
import departements from './assets/gis/departements.json';
import stations from './assets/gis/stations.json';
import { fetchFile } from './services/query';
import historical from './assets/data/historical.csv';
import historicalSc5 from './assets/data/historical-sc5.csv';
import StationChartDrawer from './components/drawerChart/StationChartDrawer';
import TourHelp from './components/TourHelp';
import PanelControl from './components/PanelControl';
import { fourDays, sc2Timestamps, sc3Timestamps, sc4Timestamps, sc5Timestamps } from './components/utils';

const App = () => {
  const [mainHistoricalData, setMainHistoricalData] = useState([]);
  const [showStations, setShowStations] = useState(true);
  const [showEmprise, setShowEmprise] = useState(true);
  const [showIsochrone, setShowIsochrone] = useState(true);
  const [showWatershed, setShowWatershed] = useState(false);
  const [showRivers, setShowRivers] = useState(false);
  const [showMeteoBasins, setShowMeteoBasins] = useState(true);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [stationsList, setStationsList] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const [selectedCompareStation, setSelectedCompareStation] = useState(null);
  const [selectedNearestRain,setSelectedNearestRain] = useState(null)
  const [selectedSearchStation, setSelectedSearchStation] = useState(null)
  const [openDrawer, setOpenDrawer] = useState(false);
  const [forecastData, setForecastData] = useState([]);
  const [compareForecastData, setCompareForecastData] = useState([]);
  const [open, setOpen] = useState(false);
  const [steps, setSteps] = useState([]);
  const [showCompareStation, setShowCompareStation] = useState(false);
  const [compareData, setCompareData] = useState([]);
  const [loadingMainChart, setLoadingMainChart] = useState(false);
  const [loadingCompareChart, setLoadingCompareChart] = useState(false);

  const scenarioBase = selectedScenario?.split(' ')[0];

  const forecastFilename= `${scenarioBase}-${selectedStation?.code}.json`;
  const compareForecastFilename= `${scenarioBase}-${selectedCompareStation?.code}.json`;

  const chartData = Object.groupBy(mainHistoricalData, ({ stationCode }) => stationCode);
  const compareChartData = Object.groupBy(compareData, ({ stationCode }) => stationCode);
  
  useEffect(() => {
    const fetchForecastData = async (station, filename, setData) => {
      if (station && selectedScenario) {
        try {
          const data = await fetchFile(scenarioBase, filename);
          setData(data);
        } catch (error) {
          console.error('Erreur lors de la récupération du fichier:', error);
        }
      }
    };
    if (selectedStation) {
      fetchForecastData(selectedStation, forecastFilename, setForecastData);
    } 
    if(selectedCompareStation)  {
      fetchForecastData(selectedCompareStation, compareForecastFilename, setCompareForecastData);
    }
  }, [selectedStation, selectedScenario, selectedCompareStation]);
  


  useEffect(() => {
    const deps = [
      { id: 'all', depCode: 'all', depName: 'tous' },
      ...departements.features.map((feature) => ({
        id: feature.properties.insee_dep,
        depCode: feature.properties.insee_dep,
        depName: feature.properties.nom
      })),
    ];
    setDepartmentsList(deps);
    setSelectedDepartments(['77', '91', '75', '94']);

    const stationsList = stations.features.map((feature) => ({
      id: feature.properties.station_id,
      stationCode: feature.properties.station_code,
      stationName: feature.properties.station_name,
    }));
    setStationsList(stationsList);
  }, []);

  useEffect(() => {
      if (selectedScenario){
        setMainHistoricalData([]);
        setCompareData([]);
      }
  }, [selectedScenario]);

  const addDataIfNotExists = (prevData, newData) => {
    const { stationCode, timestamp, value } = newData;
    const dataExists = prevData?.some(item => item.timestamp === timestamp && item.value === value && item.stationCode === stationCode);
    if (!dataExists) {
      return [...prevData, newData];
    }
    return prevData;
  };

  const processCsvRow = (row, startTimestamp, endTimestamp, stationCodes, setHistorical) => {
    const rowData = row.data;
    const rowTimestamp = rowData.timestamp;

    if (rowTimestamp >= startTimestamp && rowTimestamp <= endTimestamp) {
      stationCodes.forEach(stationCode => {
        const stationData = rowData[stationCode];
        if (stationData) {
          setHistorical(prevData =>
            addDataIfNotExists(prevData, { stationCode, timestamp: rowTimestamp, value: stationData })
          );
        }
      });
    }
  };

  const fetchDataForStations = (startTimestamp, endTimestamp, stationCodes, setLoading, setData) => {
    const file = scenarioBase === 'sc5' ? historicalSc5 : historical;
    setLoading(true);
    Papa.parse(file, {
      header: true,
      download: true,
      fastMode: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      step: (row) => processCsvRow(row, startTimestamp, endTimestamp, stationCodes, setData),
      complete: () => {
        setLoading(false);
      },
      error: (error) => {
        console.error("Error parsing CSV:", error);
        setLoading(false);
      },
    });
  };


  const scenarioTimestamps = {
    sc2: sc2Timestamps,
    sc3: sc3Timestamps,
    sc4: sc4Timestamps,
    sc5: sc5Timestamps
  };

  useEffect(() => {
    if (selectedStation && selectedScenario) {
      const { startDate, endDate } = scenarioTimestamps[scenarioBase];
      const startDateMinus4Days = startDate - fourDays;
      const endDatePlus4Days = endDate + fourDays;
      if (startDate && endDate) {
        fetchDataForStations(startDateMinus4Days, endDatePlus4Days, [selectedStation.code, selectedNearestRain], setLoadingMainChart, setMainHistoricalData);
      }
    }
  }, [selectedStation, selectedScenario]);


  useEffect(() => {
    if (selectedCompareStation && selectedScenario) {
      const { startDate, endDate } = scenarioTimestamps[scenarioBase];
      const startDateMinus4Days = startDate - fourDays;
      const endDatePlus4Days = endDate + fourDays;

      if (startDate && endDate) {
        fetchDataForStations(startDateMinus4Days, endDatePlus4Days, [selectedCompareStation.code, selectedNearestRain], setLoadingCompareChart, setCompareData);
      }
    }

  }, [selectedCompareStation, selectedScenario]);

  return (
    <div className="App">
      <PanelControl
        showStations={showStations}
        showWatershed={showWatershed} 
        showEmprise={showEmprise} 
        showRivers={showRivers}
        showMeteoBasins={showMeteoBasins}
        showIsochrone={showIsochrone}
        setSelectedDepartments={setSelectedDepartments}
        setSelectedScenario={setSelectedScenario}
        departmentsList={departmentsList}
        setShowEmprise={setShowEmprise}
        setShowIsochrone={setShowIsochrone}
        setShowMeteoBasins={setShowMeteoBasins}
        setShowStations={setShowStations}
        setShowRivers={setShowRivers}
        setShowWatershed={setShowWatershed}
        setOpen={setOpen}
        setSteps={setSteps}
        stationsList={stationsList}
        setSelectedSearchStation={setSelectedSearchStation}
      />
      
      <MapLeak 
        showStations={showStations}
        showWatershed={showWatershed} 
        showEmprise={showEmprise} 
        showRivers={showRivers}
        showMeteoBasins={showMeteoBasins}
        selectedDepartments={selectedDepartments}
        showIsochrone={showIsochrone}
        selectedStation={selectedStation}
        selectedScenario={selectedScenario}
        setOpenDrawer={setOpenDrawer}
        setSelectedStation={setSelectedStation}
        setSelectedNearestRain={setSelectedNearestRain}
        setCompareData={setCompareData}
        selectedSearchStation={selectedSearchStation}
        showCompareStation={showCompareStation}
        setSelectedCompareStation={setSelectedCompareStation}
        compareChartData={compareChartData}
        setData={setMainHistoricalData}
      />
      <StationChartDrawer
        scenarioBase={scenarioBase}
        setData={setMainHistoricalData}
        data={chartData}
        compareData={compareChartData}
        setCompareData={setCompareData}
        openDrawer={openDrawer} 
        selectedStation={selectedStation}
        selectedScenario={selectedScenario}
        setOpenDrawer={setOpenDrawer}
        forecastData={forecastData}
        compareForecastData={compareForecastData}
        setSelectedStation={setSelectedStation}
        setOpen={setOpen}
        setSteps={setSteps}
        setShowCompareStation={setShowCompareStation}
        showCompareStation={showCompareStation}
        loadingMainChart={loadingMainChart}
        loadingCompareChart={loadingCompareChart}
        selectedCompareStation={selectedCompareStation}
        setSelectedCompareStation={setSelectedCompareStation}
      />
      <TourHelp open={open} setOpen={setOpen} steps={steps} />
    </div>
  );
}

export default App;